import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFinanceApi, adminFinanceKeys } from '@/features/admin-finance/api/admin-finance.api';
import { formatCurrency } from '@/features/guide-finance/utils/formatCurrency';
import { financeListSx, financeRowSx } from '@/features/admin-finance/styles/financeSurfaces';

export function FinanceWalletsPanel() {
  const qc = useQueryClient();
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [transactionType, setTransactionType] = useState('ADJUSTMENT');
  const [reason, setReason] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: adminFinanceKeys.wallets({ page: 1 }),
    queryFn: () => adminFinanceApi.listWallets({ page: 1, limit: 50 }),
  });

  const detailQuery = useQuery({
    queryKey: adminFinanceKeys.wallets({ guideId: selectedGuideId }),
    queryFn: () => adminFinanceApi.getWalletByGuide(selectedGuideId!),
    enabled: Boolean(selectedGuideId),
  });

  const adjust = useMutation({
    mutationFn: () =>
      adminFinanceApi.adjustWallet(selectedGuideId!, {
        amount: Number(amount),
        transactionType,
        reason,
      }),
    onSuccess: () => {
      setAmount('');
      setReason('');
      void qc.invalidateQueries({ queryKey: adminFinanceKeys.all });
    },
  });

  const wallets = data?.data ?? [];

  return (
    <Box sx={financeListSx}>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 0, m: 2 }}>
          Failed to load wallets.
        </Alert>
      )}
      {!isLoading && wallets.length === 0 && (
        <Alert severity="info" sx={{ borderRadius: 0, m: 2 }}>
          No guide wallets yet.
        </Alert>
      )}

      {wallets.map((w) => (
        <Box key={w.guideId} sx={financeRowSx}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography fontWeight={700}>Guide {w.guideId.slice(-8)}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                Pending {formatCurrency(w.pendingBalance, w.currency)} · Available{' '}
                {formatCurrency(w.availableBalance, w.currency)} · Withdrawn{' '}
                {formatCurrency(w.withdrawnBalance, w.currency)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Lifetime {formatCurrency(w.totalLifetimeEarnings, w.currency)}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="outlined"
              sx={{ borderRadius: 0 }}
              onClick={() => setSelectedGuideId(w.guideId)}
            >
              Adjust
            </Button>
          </Box>
        </Box>
      ))}

      <Dialog
        open={Boolean(selectedGuideId)}
        onClose={() => setSelectedGuideId(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 0 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Adjust wallet</DialogTitle>
        <DialogContent>
          {detailQuery.data && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
              Available: {formatCurrency(detailQuery.data.wallet.availableBalance)} · Pending:{' '}
              {formatCurrency(detailQuery.data.wallet.pendingBalance)}
            </Alert>
          )}
          <TextField
            fullWidth
            type="number"
            label="Amount (positive credit / negative debit)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
          <TextField
            select
            fullWidth
            label="Type"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          >
            <MenuItem value="ADJUSTMENT">Adjustment</MenuItem>
            <MenuItem value="BONUS">Bonus</MenuItem>
            <MenuItem value="PENALTY">Penalty</MenuItem>
          </TextField>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSelectedGuideId(null)} sx={{ borderRadius: 0 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ borderRadius: 0 }}
            disabled={!amount || !reason.trim() || adjust.isPending}
            onClick={() => adjust.mutate()}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
